// iter150AZ.124 — Pure utilities for the DR404 WiFi setup dialog.
//
// Independent from the sequencing-gateway provisioning helpers in
// dr404Crc.js. Only contains:
//   * `parseWscan(raw)`          → structured hotspot list
//   * `chooseEncryption(rawEnc)` → "TKIPAES" → "AES" (prefer AES)
//   * `passwordValidatorFor`     → returns { required, validate, hint }
//   * `escapeSsid(ssid)`         → throws on commas / CR / LF
//   * `sanitizeAtForLog(cmd)`    → masks the password in AT+WSKEY
//
// All functions are pure and unit-tested in dr404Wifi.test.js.


// ─── Parsing AT+WSCAN ────────────────────────────────────────────

/**
 * Parse the body returned by ``sendAtMultiline("AT+WSCAN")`` into a
 * list of hotspot rows. iter150AZ.125 — handles TWO known firmware
 * variants:
 *
 *   * **Legacy header**: ``Ch,SSID,BSSID,Security,Indicator``
 *     Row format: ``<channel>,<ssid>,<bssid>,<auth>/<enc>,<rssi%>``
 *
 *   * **Current DR404 firmware** (header reads
 *     ``RSSI,SSID,BSSID,Channel,Encryption,Authentication``):
 *     Row format: ``<rssi%>,<ssid>,<bssid>,<channel>,<enc>,<auth>;``
 *     - rows end with ``;``
 *     - row terminator may be ``\n\r`` instead of the more usual ``\r\n``
 *     - a buggy header echo can appear at the end of the response —
 *       skip any row whose 3rd field isn't a valid BSSID.
 *
 * The BSSID (XX:XX:XX:XX:XX:XX) is the anchor we use to robustly
 * split lines whose SSID legitimately contains commas, regardless
 * of which format the firmware emitted.
 *
 * Each returned row has shape:
 *   {
 *     channel:    number,
 *     ssid:       string,
 *     bssid:      string  (lower-case),
 *     auth:       string  (e.g. "WPA2PSK", "OPEN", "SHARED"),
 *     encryption: string  (e.g. "AES", "TKIP", "NONE"),
 *     rssi:       number  (0-100 percent — higher is better),
 *     raw:        string,
 *   }
 */
export function parseWscan(rawBody) {
  const out = [];
  if (!rawBody) return out;
  const lines = String(rawBody)
    .split(/[\r\n]+/)
    .map(l => l.trim().replace(/;+$/, ""))
    .filter(Boolean);
  const bssidRe = /([0-9a-fA-F]{2}(?::[0-9a-fA-F]{2}){5})/;
  for (const line of lines) {
    const bssidMatch = line.match(bssidRe);
    if (!bssidMatch) continue;        // skips header rows + firmware echo
    const bssid = bssidMatch[1].toLowerCase();
    const bssidStart = bssidMatch.index;
    const bssidEnd = bssidStart + bssidMatch[0].length;
    const before = line.slice(0, bssidStart).replace(/,+$/, "");
    const after  = line.slice(bssidEnd).replace(/^,+/, "");
    const beforeParts = before.split(",");
    const afterParts  = after.split(",");
    let rssi, ssid, channel, auth, encryption;
    if (beforeParts[0] && /%\s*$/.test(beforeParts[0])) {
      // Current DR404 firmware: <rssi%>,<ssid>,<bssid>,<channel>,<enc>,<auth>
      rssi = parseInt(beforeParts[0].replace("%", ""), 10);
      ssid = beforeParts.slice(1).join(",");
      if (afterParts.length < 3) continue;
      channel    = parseInt(afterParts[0], 10);
      encryption = (afterParts[1] || "").trim().toUpperCase();
      auth       = (afterParts[2] || "").trim().toUpperCase();
    } else if (beforeParts[0] && /^\d+$/.test(beforeParts[0])) {
      // Legacy: <channel>,<ssid>,<bssid>,<auth>/<enc>,<rssi%>
      channel = parseInt(beforeParts[0], 10);
      ssid = beforeParts.slice(1).join(",");
      if (afterParts.length < 2) continue;
      const security = (afterParts[0] || "").trim();
      rssi = parseInt(afterParts[1], 10);
      if (security.includes("/")) {
        const [a, e] = security.split("/");
        auth       = (a || "").trim().toUpperCase();
        encryption = (e || "").trim().toUpperCase();
      } else {
        auth       = security.toUpperCase();
        encryption = "NONE";
      }
    } else {
      continue;   // unrecognised format
    }
    if (isNaN(rssi)) rssi = 0;
    if (isNaN(channel)) continue;
    out.push({ channel, ssid, bssid, auth, encryption, rssi, raw: line });
  }
  out.sort((a, b) => b.rssi - a.rssi);
  return out;
}


// ─── Encryption selection ────────────────────────────────────────

/**
 * Some DR404 firmware variants report a combined encryption value
 * such as ``TKIPAES``. The spec says: prefer AES unless the firmware
 * is confirmed to accept the combined value. We always prefer AES.
 */
export function chooseEncryption(rawEnc) {
  const e = String(rawEnc || "").toUpperCase();
  if (e === "TKIPAES" || e === "AESTKIP" || e === "TKIP+AES") return "AES";
  return e;
}


// ─── Password validation ─────────────────────────────────────────

const HEX_RE   = /^[0-9a-fA-F]+$/;
const ASCII_RE = /^[\x20-\x7e]+$/;        // printable ASCII

/**
 * Return a validation descriptor for the given AP security profile.
 *   {
 *     required:       boolean   — does the user need to enter a password?
 *     hint:           string    — UI help text (length / charset rules).
 *     validate(pwd):  string|null — null when valid, otherwise an error string.
 *   }
 */
export function passwordValidatorFor(auth, rawEnc) {
  const a = String(auth || "").toUpperCase();
  const e = chooseEncryption(rawEnc);
  // Open network — no password.
  if (a === "OPEN" && (e === "NONE" || e === "")) {
    return {
      required: false,
      hint: "Open network — no password required.",
      validate: () => null,
    };
  }
  // WEP — hex variant.
  if ((a === "OPEN" || a === "SHARED") && e === "WEP-H") {
    return {
      required: true,
      hint: "WEP hex key — 10 hex characters (40-bit) or 26 hex characters (104-bit).",
      validate: (pwd) => {
        if (!pwd) return "Password is required.";
        if (!HEX_RE.test(pwd)) return "WEP hex key must contain only 0-9 and a-f.";
        if (pwd.length !== 10 && pwd.length !== 26) {
          return "WEP hex key must be exactly 10 or 26 hex characters.";
        }
        return null;
      },
    };
  }
  // WEP — ASCII variant.
  if ((a === "OPEN" || a === "SHARED") && e === "WEP-A") {
    return {
      required: true,
      hint: "WEP ASCII key — 5 characters (40-bit) or 13 characters (104-bit).",
      validate: (pwd) => {
        if (!pwd) return "Password is required.";
        if (!ASCII_RE.test(pwd)) return "WEP ASCII key must be printable ASCII.";
        if (pwd.length !== 5 && pwd.length !== 13) {
          return "WEP ASCII key must be exactly 5 or 13 characters.";
        }
        return null;
      },
    };
  }
  // WPA / WPA2 PSK.
  if ((a === "WPAPSK" || a === "WPA2PSK" || a === "WPAPSKWPA2PSK")
       && (e === "TKIP" || e === "AES")) {
    return {
      required: true,
      hint: "WPA/WPA2 passphrase — 8 to 63 printable ASCII characters.",
      validate: (pwd) => {
        if (!pwd) return "Password is required.";
        if (!ASCII_RE.test(pwd)) return "Passphrase must be printable ASCII.";
        if (pwd.length < 8 || pwd.length > 63) {
          return "Passphrase length must be 8 to 63 characters.";
        }
        return null;
      },
    };
  }
  // Unknown profile — require a password and let the firmware decide.
  return {
    required: true,
    hint: `Unrecognised security profile (${a}/${e}) — entering a key is recommended; the firmware will reject invalid values.`,
    validate: (pwd) => (pwd ? null : "Password is required."),
  };
}


// ─── SSID safety ─────────────────────────────────────────────────

/**
 * Validate an SSID for safe inclusion in the AT command line. The
 * DR404 firmware uses comma-separated parameters and line-ending
 * detection, so SSIDs containing ``,``, ``\r`` or ``\n`` cannot be
 * safely transmitted.
 *
 * Returns the SSID unchanged if safe; throws an Error otherwise.
 */
export function escapeSsid(ssid) {
  if (typeof ssid !== "string") {
    throw new Error("SSID must be a string");
  }
  if (ssid.length === 0) {
    throw new Error("SSID cannot be empty — hidden networks are not supported by this tool.");
  }
  if (ssid.length > 32) {
    throw new Error("SSID is longer than the 802.11 max of 32 octets.");
  }
  if (/[,\r\n]/.test(ssid)) {
    throw new Error("SSID contains a comma, CR or LF — cannot be safely written via the DR404 AT command syntax.");
  }
  return ssid;
}


// ─── Log sanitisation ────────────────────────────────────────────

/**
 * Mask the password portion of ``AT+WSKEY=<auth>,<enc>,<password>``
 * so the dialog can log the AT command flow without leaking the WiFi
 * key.  All other AT commands are returned unchanged.
 *
 * iter150AZ.124 — only this one command carries the secret; SSIDs are
 * already considered non-secret.
 */
export function sanitizeAtForLog(command) {
  if (typeof command !== "string") return command;
  const m = command.match(/^(AT\+WSKEY=)([^,]+,[^,]+,)(.*)$/i);
  if (!m) return command;
  return `${m[1]}${m[2]}******`;
}


// ─── Composing the WSKEY command ─────────────────────────────────

/**
 * Build the WSKEY AT command for the given AP profile + password.
 * Returns ``{ command, sanitized }`` so the dialog can log the
 * sanitised form and write the real one.
 */
export function buildWsKeyCommand({ auth, encryption, password }) {
  const a = String(auth || "").toUpperCase();
  const e = chooseEncryption(encryption);
  if (a === "OPEN" && (e === "NONE" || e === "")) {
    return {
      command:   "AT+WSKEY=OPEN,NONE,",
      sanitized: "AT+WSKEY=OPEN,NONE,",
    };
  }
  // Defensive — the dialog also runs passwordValidatorFor() before
  // calling this, but keep an explicit guard so a programmer error
  // can't bypass it.
  if (typeof password !== "string" || password.length === 0) {
    throw new Error(`buildWsKeyCommand: password required for ${a}/${e}`);
  }
  const command = `AT+WSKEY=${a},${e},${password}`;
  return {
    command,
    sanitized: sanitizeAtForLog(command),
  };
}
